import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Search, Zap, CheckCircle2, User, Phone, Mail, FileText } from "lucide-react";
import { QuickTripCard, emptyTrip, tripComplete, tripTotalCents } from "@/components/QuickChargeTrips";

export const MAX_TRIPS = 10;

/**
 * Quick Charge — charge a regular client's card on file WITHOUT a reservation.
 * Flow: search ALL clients (name / email / phone) → pick the client → pick their
 * card on file → full trip details (required) + optional chauffeur → charge.
 * Creates a PAID quick invoice with full details as the permanent record.
 */
export default function QuickChargeTab() {
  const [q, setQ] = useState("");
  const [clients, setClients] = useState([]);
  const [searching, setSearching] = useState(false);
  const [client, setClient] = useState(null);
  const [card, setCard] = useState(null);
  // Bill-to contact (auto-filled from the client profile, fully editable)
  const [custName, setCustName] = useState("");
  const [custEmail, setCustEmail] = useState("");
  const [custPhone, setCustPhone] = useState("");
  // Trips (1..10) — each with its own route, chauffeurs (0..5), amount and "what for"
  const [trips, setTrips] = useState([emptyTrip()]);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (q.trim().length < 2) { setClients([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get("/payments/clients/search", { params: { q: q.trim() } });
        setClients(r.data || []);
      } catch { setClients([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const pickClient = (cl) => {
    setClient(cl);
    setCard(cl.cards.length === 1 ? cl.cards[0] : null);
    setCustName(cl.name || "");
    setCustEmail(cl.email || "");
    setCustPhone(cl.phone || "");
    setConfirming(false);
  };

  const reset = () => {
    setClient(null); setCard(null);
    setCustName(""); setCustEmail(""); setCustPhone("");
    setTrips([emptyTrip()]);
    setConfirming(false); setDone(null); setQ(""); setClients([]);
  };

  const totalCents = trips.reduce((sum, t) => sum + tripTotalCents(t), 0);
  const total = totalCents / 100;
  const updateTrip = (i, patch) => { setTrips(arr => arr.map((t, idx) => idx === i ? { ...t, ...patch } : t)); setConfirming(false); };
  const removeTrip = (i) => { setTrips(arr => arr.filter((_, idx) => idx !== i)); setConfirming(false); };
  const addTrip = () => { if (trips.length < MAX_TRIPS) { setTrips(arr => [...arr, emptyTrip()]); setConfirming(false); } };

  const payload = () => ({
    saved_card_id: card.saved_card_id,
    customer_name: custName.trim() || null,
    customer_email: custEmail.trim() || null,
    customer_phone: custPhone.trim() || null,
    trips: trips.map((t, i) => ({
      label: `Trip ${i + 1}`,
      pickup_address: t.pickup.trim(),
      dropoff_address: t.dropoff.trim(),
      pickup_datetime: t.when.trim(),
      stops: t.stops.map(s => ({ address: (s.address || "").trim() || null, time: (s.time || "").trim() || null })).filter(s => s.address || s.time),
      passengers: Number(t.pax),
      amount: Number(t.amount),
      description: t.description.trim(),
      chauffeurs: t.chauffeurs
        .map(c => ({ name: c.name.trim() || null, phone: c.phone.trim() || null, vehicle: c.vehicle.trim() || null }))
        .filter(c => c.name || c.phone || c.vehicle),
    })),
  });

  const previewPdf = async () => {
    setPreviewing(true);
    try {
      const r = await api.post("/payments/quick-charge/preview", payload(), { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(blobUrl, "_blank", "noopener");
    } catch (e) {
      let msg = "Preview failed";
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* noop */ }
      toast.error(msg);
    } finally { setPreviewing(false); }
  };

  const charge = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/quick-charge", payload());
      setDone(r.data);
      toast.success(`Charged $${total.toFixed(2)} to ${card.brand} ••${card.last4}${trips.length > 1 ? ` · ${trips.length} trips` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Charge failed");
      setConfirming(false);
    } finally { setBusy(false); }
  };

  const validAmount = totalCents > 0;
  const allTripsComplete = trips.every(tripComplete);
  const readyToCharge = validAmount && allTripsComplete && card;

  if (done) {
    return (
      <div className="max-w-lg" data-testid="qc-success">
        <div className="border border-emerald-500/40 bg-emerald-500/5 p-6 space-y-3">
          <div className="flex items-center gap-2 text-emerald-400">
            <CheckCircle2 size={18}/>
            <span className="text-sm font-medium">Payment successful</span>
          </div>
          <div className="text-2xl font-serif text-[#F5F2EB]">${Number(done.amount).toFixed(2)}</div>
          <div className="text-xs text-[#A1A1AA] space-y-1">
            <div>{done.customer_name} · card ••{done.last4}</div>
            <div>Invoice <span className="text-[#D4AF37]">{done.invoice_number}</span> · Reservation <span className="text-[#D4AF37]">{done.confirmation_code}</span></div>
            {done.sms_status === "sent" && (
              <div data-testid="qc-sms-sent" className="text-emerald-400">Receipt texted to {done.customer_phone}</div>
            )}
            {done.sms_status && done.sms_status !== "sent" && (
              <div data-testid="qc-sms-status" className="text-amber-400/90">
                Receipt text not sent ({done.sms_status === "skipped:no_phone" ? "no phone on file" : done.sms_status}) — use the Invoices tab to resend.
              </div>
            )}
            {done.email_status === "sent" && (
              <div data-testid="qc-email-sent" className="text-emerald-400">Receipt emailed to {done.customer_email}</div>
            )}
            <div>Find it in the <b>Invoices</b> tab to download the PDF or email/text the receipt.</div>
            <div className="text-[#71717A]">Square payment: {done.payment_id}</div>
          </div>
          <button data-testid="qc-new-charge" onClick={reset} className="btn-gold !py-2 !px-4 text-[10px]">
            <Zap size={12}/> New quick charge
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-5" data-testid="quick-charge-tab">
      <div>
        <h2 className="text-lg text-[#F5F2EB] font-serif">Quick Charge</h2>
        <p className="text-xs text-[#A1A1AA] mt-1">
          For special cases — a regular client took a ride without a reservation and asked to be
          charged on their card on file. Creates a paid invoice with full details as the receipt.
        </p>
      </div>

      {/* 1 · Find the client */}
      <div className="border border-white/10 bg-[#0A0A0A] p-5">
        <div className="overline mb-3">1 · Find the client</div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]"/>
          <input data-testid="qc-search" className="field-input !pl-9" placeholder="Search by name, email, or phone — e.g. billy"
            value={q} onChange={e => { setQ(e.target.value); setClient(null); setCard(null); }}/>
        </div>
        {searching && <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] mt-2">Searching…</div>}
        {!searching && q.trim().length >= 2 && clients.length === 0 && (
          <div className="text-xs text-[#71717A] mt-2" data-testid="qc-no-clients">
            No clients match "{q}". Check the spelling or add the client from the Users tab.
          </div>
        )}
        {clients.length > 0 && (
          <div className="mt-3 space-y-2" data-testid="qc-client-list">
            {clients.map(cl => (
              <button key={cl.email} data-testid={`qc-client-${cl.email}`}
                onClick={() => pickClient(cl)} disabled={!cl.cards.length}
                className={`w-full flex items-center gap-3 border p-3 text-left transition ${client?.email === cl.email ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 hover:border-white/30"} ${!cl.cards.length ? "opacity-50 cursor-not-allowed" : ""}`}>
                <User size={16} className="text-[#D4AF37] shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#F5F2EB] truncate">{cl.name || "Client"}</div>
                  <div className="text-[10px] text-[#71717A] truncate flex items-center gap-2">
                    {cl.email && <span className="inline-flex items-center gap-1"><Mail size={9}/>{cl.email}</span>}
                    {cl.phone && <span className="inline-flex items-center gap-1"><Phone size={9}/>{cl.phone}</span>}
                  </div>
                </div>
                <div className="text-xs text-[#A1A1AA] tabular-nums shrink-0">
                  {cl.cards.length
                    ? cl.cards.map(c => <div key={c.saved_card_id}>{c.brand} ••{c.last4}</div>)
                    : <span className="text-[#71717A]">No card on file</span>}
                </div>
              </button>
            ))}
          </div>
        )}
        {client && client.cards.length > 1 && (
          <div className="mt-3" data-testid="qc-card-picker">
            <div className="field-label mb-1.5">Which card?</div>
            <div className="space-y-2">
              {client.cards.map(c => (
                <button key={c.saved_card_id} data-testid={`qc-card-${c.saved_card_id}`}
                  onClick={() => { setCard(c); setConfirming(false); }}
                  className={`w-full flex items-center gap-3 border p-2.5 text-left transition ${card?.saved_card_id === c.saved_card_id ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 hover:border-white/30"}`}>
                  <CreditCard size={14} className="text-[#D4AF37] shrink-0"/>
                  <span className="text-xs text-[#F5F2EB]">{c.brand} ••{c.last4}</span>
                  <span className="text-[10px] text-[#71717A]">{c.exp_month}/{String(c.exp_year).slice(-2)}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 2 · Bill-to contact — auto-filled from profile, editable */}
      {client && card && (
        <div className="border border-white/10 bg-[#0A0A0A] p-5" data-testid="qc-contact-form">
          <div className="overline mb-3">2 · Client contact <span className="text-[#71717A] normal-case tracking-normal text-[10px] ml-1">(auto-filled from profile — shown on the invoice, edit as needed)</span></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="field-label">Name</label>
              <input data-testid="qc-cust-name" className="field-input" placeholder="Client name"
                value={custName} onChange={e => setCustName(e.target.value)}/>
            </div>
            <div>
              <label className="field-label">Email</label>
              <input data-testid="qc-cust-email" type="email" className="field-input" placeholder="client@email.com"
                value={custEmail} onChange={e => setCustEmail(e.target.value)}/>
            </div>
            <div>
              <label className="field-label">Phone</label>
              <input data-testid="qc-cust-phone" className="field-input" placeholder="(414) 555-0100"
                value={custPhone} onChange={e => setCustPhone(e.target.value)}/>
            </div>
          </div>
        </div>
      )}

      {/* 3 · Trips — REQUIRED, one card per trip (up to 10), each with its own chauffeurs, amount & "what for" */}
      {client && card && (
        <div className="border border-white/10 bg-[#0A0A0A] p-5" data-testid="qc-trip-form">
          <div className="overline mb-3 flex items-center gap-2 flex-wrap">
            3 · Trip details
            <span className="text-red-400/80 normal-case tracking-normal text-[10px]">required — each trip is its own line on the invoice</span>
            <span className="ml-auto text-[10px] text-[#71717A] normal-case tracking-normal" data-testid="qc-trip-count">{trips.length} of {MAX_TRIPS} trips</span>
          </div>
          <div className="space-y-4">
            {trips.map((t, i) => (
              <QuickTripCard key={i} index={i} trip={t} canRemove={trips.length > 1}
                onChange={patch => updateTrip(i, patch)} onRemove={() => removeTrip(i)}/>
            ))}
          </div>
          <button data-testid="qc-add-trip" onClick={addTrip} disabled={trips.length >= MAX_TRIPS}
            className="btn-ghost !py-2 !px-3 text-[10px] mt-4 disabled:opacity-40"
            title={trips.length >= MAX_TRIPS ? `Maximum ${MAX_TRIPS} trips per quick charge` : "Add another trip to this charge"}>
            + Add another trip
          </button>
        </div>
      )}

      {/* 4 · Total + charge (auto-summed from all trips) */}
      {client && card && (
        <div className="border border-white/10 bg-[#0A0A0A] p-5" data-testid="qc-charge-form">
          <div className="overline mb-3">4 · Charge {custName || client.email} — {card.brand} ••{card.last4}</div>
          <div className="border border-[#D4AF37]/25 bg-[#D4AF37]/[0.04] p-3">
            <div className="grid grid-cols-[auto_1fr_auto] gap-x-3 gap-y-1 text-xs">
              {trips.map((t, i) => (
                <React.Fragment key={i}>
                  <span className="text-[#D4AF37] uppercase tracking-[0.15em] text-[10px]">Trip {i + 1}</span>
                  <span className="text-[#A1A1AA] truncate">{[t.pickup.trim(), t.dropoff.trim()].filter(Boolean).join(" to ") || "—"}{t.description.trim() ? ` · ${t.description.trim()}` : ""}</span>
                  <span className="text-right text-[#F5F2EB]" data-testid={`qc-summary-amount-${i}`}>${(tripTotalCents(t) / 100).toFixed(2)}</span>
                </React.Fragment>
              ))}
              <span className="col-span-2 pt-2 mt-1 border-t border-white/10 text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Total charge{trips.length > 1 ? ` · ${trips.length} trips` : ""}</span>
              <span className="pt-2 mt-1 border-t border-white/10 text-right font-serif text-xl text-[#D4AF37]" data-testid="qc-total">${total.toFixed(2)}</span>
            </div>
          </div>
          {!allTripsComplete && (
            <p className="text-[10px] text-amber-400/90 mt-3" data-testid="qc-trip-incomplete">
              Complete every trip above (pickup, date &amp; time, passengers, drop-off, amount, what for) to enable the charge.
            </p>
          )}
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <button data-testid="qc-preview-btn" disabled={!readyToCharge || previewing || busy}
              onClick={previewPdf}
              className="btn-ghost !py-2.5 !px-4 text-[11px] disabled:opacity-40">
              <FileText size={13}/> {previewing ? "Rendering…" : "Preview receipt"}
            </button>
            {!confirming ? (
              <button data-testid="qc-charge-btn" disabled={!readyToCharge || busy}
                onClick={() => setConfirming(true)}
                className="btn-gold !py-2.5 !px-5 text-[11px] disabled:opacity-40">
                <Zap size={13}/> Charge {validAmount ? `$${total.toFixed(2)}` : "card"}
              </button>
            ) : (
              <>
                <button data-testid="qc-confirm-btn" disabled={busy} onClick={charge}
                  className="btn-gold !py-2.5 !px-5 text-[11px] disabled:opacity-40">
                  {busy ? "Charging…" : `Confirm — charge $${total.toFixed(2)} to ${card.brand} ••${card.last4}`}
                </button>
                <button data-testid="qc-cancel-btn" disabled={busy} onClick={() => setConfirming(false)}
                  className="btn-ghost !py-2.5 !px-4 text-[11px]">Cancel</button>
              </>
            )}
          </div>
          <p className="text-[10px] text-[#71717A] mt-3">
            The card is charged once for the combined total via Square. A paid invoice listing every trip (with its chauffeurs) is created in the Invoices tab.
          </p>
        </div>
      )}
    </div>
  );
}
